import Link from "next/link";
import { useRouter } from "next/router";
import { useSelector } from "react-redux";
import { FiCompass, FiEdit3, FiActivity, FiUser, FiMessageCircle, FiGrid } from "react-icons/fi";
import { assistantOn } from "../assistant/api";
import { PLACES, placeOf, placeHref, shownTo } from "./places";
import { Area, Part, usePlatform, usePlatformVersion, useFilter } from "../platform/react";
import { useModerationCount } from "./moderation-count";
import styles from "./places.module.css";

const ICONS = { explore: FiCompass, make: FiEdit3, play: FiActivity, you: FiUser };
const BRAND = { home: "/community", logo: null };

// The menu: the four places that never change (docs/refactor-plan.md), then Tools (the tool library,
// Ctrl+K) and Ask (the assistant). A rail down the left on screens 760px and wider, a bar along the
// bottom on a phone (where tools are in Make, and the assistant keeps its own button). Each place's
// tabs are across the top of its pages (PlaceTabs).
export default function Navigation() {
  const router = useRouter();
  const user = useSelector((state) => state.realm.currentUser);
  const pending = useModerationCount(user?.isAdmin, router.asPath);
  const site = usePlatform();
  usePlatformVersion();
  // The places a site has (an app turned off goes from the menu, nav.places changes them), and the
  // links plugins add down the rail (nav.items). `brand` is the logo and where it goes (api.brand).
  const places = useFilter("nav.places", PLACES), extra = useFilter("nav.items", []), brand = { ...BRAND, ...site.branding() }, current = placeOf(router.pathname, router.query, places);
  return <nav id="site-navigation" className={styles.rail} aria-label="Site navigation">
    <Link href={brand.home} className={styles.brand} aria-label={site.name}>{brand.logo ? <img className={styles.brandLogo} src={brand.logo} alt="" /> : <span className={styles.brandMark} aria-hidden="true" />}</Link>
    <ul className={styles.places}>
      {places.map((place) => {
        const Icon = ICONS[place.id] || FiGrid, waiting = place.id === "you" ? pending : 0, href = placeHref(place, site.appEnabled);
        if (!href) return null;
        return <li key={place.id}><Link href={href} className={styles.place} aria-current={place.id === current ? "true" : undefined} aria-label={waiting ? `${place.label}, ${waiting} waiting` : undefined}>
          <span className={styles.icon}><Icon aria-hidden="true" />{waiting > 0 && <span className={styles.badge} aria-hidden="true">{waiting > 99 ? "99+" : waiting}</span>}</span>{place.label}</Link></li>;
      })}
      {extra.filter((item) => item?.href && item?.label && shownTo(item.who, user)).map((item) => <li key={item.id || item.href}><Link href={item.href} className={styles.place} data-plugin-nav={item.id || ""}>
        <span className={styles.icon}><FiGrid aria-hidden="true" /></span>{item.label}</Link></li>)}
    </ul>
    <div className={styles.railEnd}>
      <Area name="site.rail">
        <Part name="site.rail.tools" label="Tools"><button type="button" className={styles.place} aria-label="Tools" aria-keyshortcuts="Control+K Meta+K" title="Tools (Ctrl+K)" onClick={() => window.dispatchEvent(new Event("truechops:tools"))}>
          <span className={styles.icon}><FiGrid aria-hidden="true" /></span>Tools<kbd className={styles.keys}>Ctrl K</kbd></button></Part>
        {assistantOn() && site.appEnabled("assistant") && <Part name="site.rail.ask" label="Ask"><button type="button" className={styles.place} aria-keyshortcuts="Control+/ Meta+/" title="Ask the assistant (Ctrl+/)" onClick={() => window.dispatchEvent(new Event("truechops:ask"))}>
          <span className={styles.icon}><FiMessageCircle aria-hidden="true" /></span>Ask</button></Part>}
      </Area>
    </div>
  </nav>;
}
